import type { Layout, JsonSchema } from '@jsonforms/core';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { OpenClose } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { InstrumentFormState } from '@thinkalpha/ui-types/types/instrument';
import type { ErrorObject } from 'ajv';
import type { SymbolPickerState, InstrumentType } from 'src/features/symbol-picker/state/types';
import { parseInstrumentToFormState } from 'src/lib/instrumentUtils';

export type BaseSchema = Record<string, any>;

export enum OrderAction {
    Buy = 'buy',
    Sell = 'sell',
    SellShort = 'sell-short',
    MultiLeg = 'multi-leg',
}

export type FormData = Record<string, any>;

export interface OrderData extends FormData {
    instrument: Instrument | undefined;
    account: string;
    side: OrderAction;
    exDestination: string;
    route: string;
    type: string;
}

export interface MarketData {
    bid: number | undefined;
    ask: number | undefined;
    last: number | undefined;
}

export type PriceTicker = keyof MarketData | 'mid';

// Shared base state properties
interface TradeProStateBase {
    showTradeToast: boolean;
    skipTradeConfirmation: boolean;
    orderErrors: ErrorObject[];
    executorsSchema: Record<string, any>;
    executorsSchemaIsLoading: boolean;
    executorsSchemaFetchingError: Error | null;
    fallbackToBasicSchema: boolean;
    formRenderFailureCount: number;
    preFallbackOrderData: OrderData | null;
    exDestinationsWithRoutes: Record<string, string[]>;
    allowedInstrumentTypes: InstrumentType[];
    selectedExecutorSchema: Record<string, any>;
    schema: JsonSchema;
    uischema: Layout;
    shouldPopulateDefaultPrice: boolean;
    defaultPriceSource: PriceTicker;
    marketData: MarketData;
    selectedAccountEffectiveAccess: PermissionType;
    hasCompletedInitialSchemaFetch: boolean;
    lastRawExecutorSchema: BaseSchema | undefined;
}

// Single-leg state
export interface TradeProSingleLegState extends TradeProStateBase {
    type: 'single-leg';
    orderData: OrderData;
    initialOrderDataSnapshot: OrderData | null;
}

// Multi-leg specific types
export interface BaseLegData {
    id?: string;
    symbolPickerState: SymbolPickerState;
    side: OrderAction;
    quantity?: number;
    quantityRatio: number;
    price?: number;
    type?: 'market' | 'limit' | 'stop' | 'stop_limit';
}

export interface OptionsLegData extends BaseLegData {
    openClose?: OpenClose;
}

export abstract class BaseLeg {
    id: string;
    symbolPickerState: SymbolPickerState; // Single source of truth for instrument
    side: OrderAction;
    quantity?: number;
    quantityRatio: number; // For ratio spreads
    price?: number;
    type?: 'market' | 'limit' | 'stop' | 'stop_limit';

    protected constructor(data: BaseLegData) {
        this.id = data.id ?? crypto.randomUUID();
        this.symbolPickerState = data.symbolPickerState;
        this.side = data.side;
        this.quantity = data.quantity;
        this.quantityRatio = data.quantityRatio;
        this.price = data.price;
        this.type = data.type;
    }

    // Computed property: derive instrument from symbolPickerState
    // Returns instrument with expiry as string (form state) for validation compatibility
    get instrument(): InstrumentFormState | undefined {
        if (!this.symbolPickerState.symbolString) {
            return undefined;
        }

        try {
            return parseInstrumentToFormState(this.symbolPickerState.symbolString);
        } catch {
            return undefined;
        }
    }
}

export class OptionLeg extends BaseLeg {
    openClose?: OpenClose;

    constructor(data: OptionsLegData) {
        super(data);

        this.openClose = data.openClose;
    }
}

export class EquityLeg extends BaseLeg {
    constructor(data: BaseLegData) {
        super(data);
    }
}

export function createLeg(data: BaseLegData & { openClose?: OpenClose }): OptionLeg | EquityLeg {
    if (data.symbolPickerState.instrument.instrumentType === 'options') {
        const { openClose, ...baseData } = data;
        const legData: OptionsLegData = { ...baseData };
        if (openClose !== undefined) {
            legData.openClose = openClose;
        }
        return new OptionLeg(legData);
    } else {
        const { openClose: _openClose, ...equityData } = data;
        return new EquityLeg(equityData);
    }
}

export type Leg = OptionLeg | EquityLeg;

export interface MultiLegOrderData extends OrderData {
    legs: Leg[];
}

// Multi-leg state
export interface TradeProMultiLegState extends TradeProStateBase {
    type: 'multi-leg';
    orderData: MultiLegOrderData;
    initialOrderDataSnapshot: MultiLegOrderData | null;
    activeStrategy: string;
}

// Union type
export type TradeProState = TradeProSingleLegState | TradeProMultiLegState;

export const Ratios = ['1:1', '1:2', '1:3', '1:4'] as const;
export type Ratio = (typeof Ratios)[number];
